import React, { useEffect, useMemo, useState, useRef } from "react";
import AILightComponent from '../../components/AILightComponent';

const ROLES = [
  // Individual layer
//   "student",
//   "parent",
  "employee",
  "customer",

  // Institutional layer
//   "teacher",
//   "warden",
//   "admin",
  "institution",
  "compliance_officer",
  "auditor",
  "hr",
  "finance_officer",

  // BFSI / Enterprise layer
  "bank_user",
  "kyc_officer",
  "risk_analyst",
  "branch_manager",
  "regulator",
  "ops_team",
];

const DEFAULT_STATS = {
  criticalSignals: 0,
  mediumSignals: 0,
  lowSignals: 0,
  totalSignals: 0,
};

const AnugyaAI = ({ selectedLanguage = "en" }) => {
  const [userRole, setUserRole] = useState("student");

  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState("");
  const [stats, setStats] = useState(DEFAULT_STATS);

  const [agentStatus, setAgentStatus] = useState("");
  const [actions, setActions] = useState([]);
  const [agentState, setAgentState] = useState(null);
  const [showAgentPopup, setShowAgentPopup] = useState(false);

  const [agentPipeline, setAgentPipeline] = useState([]);
  const [toolExecutions, setToolExecutions] = useState([]);

  const [memoryTimeline, setMemoryTimeline] = useState([]);
  const [reflections, setReflections] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const prevStatusRef = useRef(null);

  useEffect(() => {
    if (agentState?.status && agentState.status !== prevStatusRef.current) {
      setShowAgentPopup(true);
      setTimeout(() => setShowAgentPopup(false), 2000);
      prevStatusRef.current = agentState.status;
    }
  }, [agentState]);

  // LIVE SNAPSHOT LOOP (Anugya Cognitive Stream)
  useEffect(() => {
    const interval = setInterval(async () => {
      const res = await fetch("http://localhost:3001/api/anugya-mock/snapshot");
      const data = await res.json();

      setStats({
        criticalSignals: data.criticalSignals || 0,
        mediumSignals: data.mediumSignals || 0,
        lowSignals: data.lowSignals || 0,
        totalSignals:
          (data.criticalSignals || 0) +
          (data.mediumSignals || 0) +
          (data.lowSignals || 0),
      });

      setSummary(data.summary);
      setAgentState(data);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const fetchAIInsights = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch("http://localhost:3001/api/ai/query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role: userRole,
          query: `${userRole} governance risk + compliance + behavioral signal analysis`,
        }),
      });

      if (!response.ok) throw new Error("AI request failed");

      const data = await response.json();

      setSignals(data.signals || data.cognition?.signals || []);
      setSummary(data.summary || data.cognition?.summary || "");

      setStats({
        criticalSignals:
          data.criticalSignals ?? data.cognition?.criticalSignals ?? 0,
        mediumSignals:
          data.mediumSignals ?? data.cognition?.mediumSignals ?? 0,
        lowSignals:
          data.lowSignals ?? data.cognition?.lowSignals ?? 0,
        totalSignals:
          data.totalSignals ?? data.cognition?.totalSignals ?? 0,
      });

      setAgentStatus(data.status || "");
      setActions(data.actions || []);
    } catch (err) {
      setError("Failed to load Anugya cognitive insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAIInsights();
  }, [userRole]);

  const severityColor = (severity) => {
    switch ((severity || "").toLowerCase()) {
      case "high":
        return "text-red-400";
      case "medium":
        return "text-yellow-400";
      case "low":
        return "text-green-400";
      default:
        return "text-gray-400";
    }
  };

  const safeText = (value) =>
    typeof value === "string"
      ? value
      : value?.[selectedLanguage] || value?.en || "N/A";

  const governanceState = useMemo(() => {
    if (stats.criticalSignals >= 5) {
      return { label: "Systemic Governance Risk", color: "bg-red-900" };
    }
    if (stats.criticalSignals >= 2) {
      return { label: "Elevated Compliance Risk", color: "bg-yellow-900" };
    }
    return { label: "Governance Stable", color: "bg-green-900" };
  }, [stats]);

  const [sendToChatOpen, setSendToChatOpen] = useState(false);
  const [sendToChat, setSendToChat] = useState(false);

  const buildChatPayload = () => {
  if (!signals || signals.length === 0) {
    console.warn("⚠️ No signals found in state");
  }

  return (signals || []).map((s) => ({
    type: s.type,
    severity: s.severity,
    insight: s.insight,
    role: s.role,
    context: s.context,
  }));
};

  return (
    <div className="bg-gray-950 rounded-2xl p-4 text-white space-y-4">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-bold text-cyan-300">
            Anugya AI Signals Layer
          </h2>
          <p className="text-xs text-gray-400">
            Governance • Compliance • Behavioral Intelligence
          </p>
        </div>

        <select
          value={userRole}
          onChange={(e) => setUserRole(e.target.value)}
          className="bg-gray-800 px-3 py-1 rounded-lg text-sm"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r.toUpperCase()}
            </option>
          ))}
        </select>
      </div>
      <div className="flex justify-end mb-2 gap-2">
        {!sendToChatOpen ? (
          <button
            onClick={() => setSendToChatOpen(true)}
            className="bg-cyan-700 hover:bg-cyan-600 text-white text-xs px-3 py-2 rounded-lg"
          >
            Send Signals to GenAIs
          </button>
        ) : (
          <button
            onClick={() => setSendToChatOpen(false)}
            className="bg-red-700 hover:bg-red-600 text-white text-xs px-3 py-2 rounded-lg"
          >
            Close Analysis
          </button>
        )}

      </div>
      {sendToChatOpen && (
        <div className="mt-4 border border-cyan-800 rounded-xl p-3 bg-black relative">

          {/* CLOSE BUTTON */}
          <button
            onClick={() => setSendToChatOpen(false)}
            className="bg-red-700 hover:bg-red-900 text-white text-[10px] px-2 py-2 w-12 tetx-xs lg:text-sm rounded-md"
          >
            ✕ 
          </button>

          <AILightComponent
            param1={"document_consent" + userRole}
            param2={JSON.stringify(buildChatPayload(), null, 2)}
          />
        </div>
      )}
      {/* GOVERNANCE STATE */}
      <div className={`${governanceState.color} p-4 rounded-xl`}>
        <div className="text-sm">Governance Status</div>
        <div className="text-lg font-semibold">
          {governanceState.label}
        </div>
      </div>

      {/* ================= AGENTIC AI LAYER (ADD-ON) ================= */}
      {agentState?.agents && (
        <div className="bg-black/40 border border-cyan-900 rounded-2xl p-4">

          <h3 className="text-cyan-300 text-sm font-bold">
            🧠 Anugya Agentic Intelligence Layer
          </h3>

          <p className="text-[10px] text-gray-400 mt-1">
            Multi-agent governance reasoning system (live from backend)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">

            {agentState.agents.map((agent, i) => (
              <div
                key={i}
                className="bg-slate-900 border border-slate-700 rounded-xl p-3"
              >
                <div className="flex justify-between items-center">
                  <div className="text-xs font-bold text-yellow-200">
                    {agent.name}
                  </div>

                  <div className={`text-[10px] px-2 py-1 rounded ${
                    agent.status === "active"
                      ? "bg-green-700 text-white"
                      : agent.status === "running"
                      ? "bg-blue-700 text-white"
                      : "bg-gray-700 text-gray-300"
                  }`}>
                    {agent.status}
                  </div>
                </div>

                <div className="text-[10px] text-gray-300 mt-2">
                  {agent.summary}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-red-800 p-3 rounded-xl">
          <div className="text-xl">{stats.criticalSignals}</div>
          <div className="text-xs">Critical</div>
        </div>

        <div className="bg-yellow-700 p-3 rounded-xl">
          <div className="text-xl">{stats.mediumSignals}</div>
          <div className="text-xs">Medium</div>
        </div>

        <div className="bg-green-700 p-3 rounded-xl">
          <div className="text-xl">{stats.lowSignals}</div>
          <div className="text-xs">Low</div>
        </div>

        <div className="bg-blue-700 p-3 rounded-xl">
          <div className="text-xl">{stats.totalSignals}</div>
          <div className="text-xs">Total</div>
        </div>
      </div>

      {/* SUMMARY */}
      {summary && (
        <div className="bg-purple-900 p-4 rounded-xl">
          <div className="text-yellow-300 text-sm">AI Governance Summary</div>
          <div className="text-sm mt-2">{summary}</div>
        </div>
      )}

      {/* SIGNALS */}
      <div className="space-y-3">
        {signals.length === 0 && !loading && (
          <div className="bg-gray-800 p-4 rounded-xl text-gray-400 text-sm">
            No governance anomalies detected
          </div>
        )}

        {signals.map((s, i) => (
          <div key={i} className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <div className="flex justify-between">
              <div className="text-cyan-300">{safeText(s.type)}</div>
              <div className={`text-xs ${severityColor(s.severity)}`}>
                {s.severity}
              </div>
            </div>

            <div className="text-sm text-gray-300 mt-2">
              {safeText(s.insight)}
            </div>

            <div className="text-[10px] text-gray-500 mt-2">
              {s.source || "Anugya Cognitive Engine"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnugyaAI;